import React from "react";
import ReactDOM from "react-dom/client";
import "@/index.css";
import App from "@/App";

const root = ReactDOM.createRoot(document.getElementById("root"));

// Crash-proof against browser auto-translate (Google Translate / in-app
// translators). They mutate/reparent text nodes behind React's back, so when
// React later removes or reorders nodes it throws NotFoundError on
// removeChild/insertBefore and the whole app white-screens into the
// ErrorBoundary. Guarding these two DOM methods lets auto-translate keep
// working without crashing React. (facebook/react#11538)
if (typeof Node === 'function' && Node.prototype) {
  const originalRemoveChild = Node.prototype.removeChild;
  Node.prototype.removeChild = function (child) {
    if (child.parentNode !== this) {
      return child;
    }
    return originalRemoveChild.apply(this, arguments);
  };
  const originalInsertBefore = Node.prototype.insertBefore;
  Node.prototype.insertBefore = function (newNode, referenceNode) {
    if (referenceNode && referenceNode.parentNode !== this) {
      return newNode;
    }
    return originalInsertBefore.apply(this, arguments);
  };
}

// Global safety-net: recover from stale lazy-chunk failures automatically, with
// NO user action required. Stale chunks surface two ways — as unhandled promise
// rejections (dynamic import()) and as resource-load `error` events (<script>/
// <link>). Either way we purge caches, drop the stale service worker, and reload
// once so the freshest build is fetched. Guarded per-session to avoid loops; a
// full app relaunch (which clears sessionStorage) re-arms it, so simply
// reopening the app self-heals without reinstalling.
const CHUNK_RE = /Loading chunk [\w-]+ failed|Loading CSS chunk|ChunkLoadError|import\(\) failed|Failed to fetch dynamically imported module|error loading dynamically imported module/i;

const recoverFromStaleBuild = () => {
  if (sessionStorage.getItem('chunkReloaded')) return;
  sessionStorage.setItem('chunkReloaded', '1');
  (async () => {
    try {
      if ('caches' in window) {
        const keys = await caches.keys();
        await Promise.all(keys.map((k) => caches.delete(k)));
      }
    } catch (e) { /* ignore */ }
    try {
      if ('serviceWorker' in navigator) {
        const regs = await navigator.serviceWorker.getRegistrations();
        await Promise.all(regs.map((r) => r.unregister()));
      }
    } catch (e) { /* ignore */ }
    window.location.reload();
  })();
};

window.addEventListener('unhandledrejection', (event) => {
  const reason = event?.reason;
  const msg = (reason && (reason.message || reason.toString())) || '';
  if (reason?.name === 'ChunkLoadError' || CHUNK_RE.test(msg)) {
    recoverFromStaleBuild();
  }
});

// Resource-load failures (a hashed JS/CSS chunk that 404s after a redeploy) fire
// an `error` event on the element, which does NOT bubble — listen in the capture
// phase and only act on our own /static/ assets so unrelated failures are ignored.
window.addEventListener('error', (event) => {
  const target = event?.target;
  if (target && (target.tagName === 'SCRIPT' || target.tagName === 'LINK')) {
    const src = target.src || target.href || '';
    if (src.includes('/static/')) {
      recoverFromStaleBuild();
      return;
    }
  }
  if (CHUNK_RE.test(event?.message || '')) {
    recoverFromStaleBuild();
  }
}, true);

root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>,
);

// Register service worker for offline support and faster loading.
// Self-updating strategy for installed PWAs (which are suspended, not closed,
// so they otherwise never notice a new deploy):
//   1. Check for a new worker on launch AND every time the app returns to the
//      foreground, plus a periodic safety-net poll.
//   2. When a genuinely new worker takes control, reload ONCE so the fresh JS
//      bundle is picked up — guarded so it never loops and never fires on the
//      very first install.
if ('serviceWorker' in navigator) {
  // Only treat a controller swap as an "update" for returning visitors who
  // already had a controller — a first-ever install must not trigger a reload.
  const hadController = !!navigator.serviceWorker.controller;
  let refreshing = false;
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (refreshing || !hadController) return;
    refreshing = true;
    window.location.reload();
  });

  window.addEventListener('load', () => {
    navigator.serviceWorker
      // updateViaCache:'none' is CRITICAL for installed PWAs: it forces the
      // browser to ALWAYS bypass the HTTP cache when fetching service-worker.js
      // during an update check. Without it, a cacheable SW script response left
      // the installed app re-reading stale SW bytes → the new worker never
      // installed and the PWA stayed frozen on old code (while web browsers,
      // which revalidate the shell, saw the update). This is the root cause of
      // "updates work on web but not in the installed PWA".
      .register('/service-worker.js', { updateViaCache: 'none' })
      .then((registration) => {
        const fireUpdate = (worker) => {
          window.dispatchEvent(new CustomEvent('lumera:sw-update-ready', {
            detail: { worker },
          }));
        };

        // Kick an immediate update check on launch.
        registration.update().catch(() => {});

        // A new worker may already be waiting from a previous load.
        if (registration.waiting && navigator.serviceWorker.controller) {
          fireUpdate(registration.waiting);
        }

        registration.addEventListener('updatefound', () => {
          const installing = registration.installing;
          if (!installing) return;
          installing.addEventListener('statechange', () => {
            if (installing.state === 'installed' && navigator.serviceWorker.controller) {
              fireUpdate(installing);
            }
          });
        });

        // Installed PWAs are resumed rather than reloaded — so re-check for a
        // new deploy whenever the app comes back to the foreground, and poll
        // hourly as a safety net. The SW auto-activates (skipWaiting), which
        // fires controllerchange → the guarded one-time reload above.
        const checkForUpdate = () => registration.update().catch(() => {});
        document.addEventListener('visibilitychange', () => {
          if (document.visibilityState === 'visible') checkForUpdate();
        });
        window.addEventListener('focus', checkForUpdate);
        setInterval(checkForUpdate, 60 * 60 * 1000);
      })
      .catch((error) => {
        console.error('ServiceWorker registration failed:', error);
      });
  });
}
